"use client";

import * as React from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateAgency } from "@/hooks/use-agencies";
import type { CreateAgencyDto } from "@/types";

interface AddAgencyDialogProps {
  children?: React.ReactNode;
  onSuccess?: () => void;
}

/**
 * Create an agency — name only.
 *
 * ⚠️ There is NO Monday.com token field here any more, and it must not come
 * back (S22-05-05). An agency now sets, replaces and removes its own token
 * from its organisation settings, where the token is validated against Monday
 * before it is stored — a guarantee this console could never make, because
 * nothing here could display or verify a stored credential.
 *
 * The backend still ACCEPTS `mondayToken` on this route, deliberately, so that
 * removing it from the client did not need a coordinated deploy. Dropping it
 * server-side is its own ticket and comes after this is live. Tokens set
 * through this dialog before it went keep working untouched.
 */
export function AddAgencyDialog({ children, onSuccess }: AddAgencyDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");

  const mutation = useCreateAgency();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Agency name is required");
      return;
    }

    const payload: CreateAgencyDto = { name: trimmedName };

    // POST /agencies answers with the id only, so the toast names the value
    // that was submitted rather than reading a name back off the response.
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(`Agency "${trimmedName}" created successfully!`);
        setName("");
        setOpen(false);
        onSuccess?.();
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create agency";
        toast.error(errorMessage);
        console.error("Error creating agency:", error);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children || <Button>Add Agency</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Agency</DialogTitle>
          <DialogDescription>
            Create a new agency. Fill in the details below.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Agency Name */}
            <div className="grid gap-2">
              <Label htmlFor="agency-name">
                Agency Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="agency-name"
                placeholder="e.g., Awestruck"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Creating..." : "Create Agency"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
